import { useState } from "react";
import { LogOut, Maximize, Mic, MicOff, Minimize, Minimize2, PanelRightClose, PanelRightOpen, Pause, Phone, PhoneOff, Play } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../i18n/I18nProvider";
import { formatPhone } from "../../lib/phone";
import { Avatar, IconBtn, fmtDuration } from "../ui";
// Naming an error and reacting to one are the same question, so both live with
// the classifier in callError.ts. This file only asks for the short line.
import { errorStatusKey } from "./callError";
import type { CallAudio } from "./audio/apply";
import { CallControls } from "./CallControls";
import { CallErrorBox } from "./CallErrorBox";
import { CallNoteBox } from "./CallNoteBox";
import { EndOfCall } from "./EndOfCall";
import { RecordingStop } from "./RecordingStop";
import { TransferPad } from "./TransferPad";
import { CallWave } from "./wave/CallWave";
import { PhoneContextPanel } from "./context";
import { CallOutcome } from "./queue/CallOutcome";
import type { CallQueue } from "./queue/queue";
import type { CallOutcomeApi } from "./queue/useCallOutcome";
import type { CallPhase, CallState } from "./CallProvider";
import { useElapsed } from "./useElapsed";

const DTMF = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"];

/**
 * The call itself: one centred column.
 *
 * Only the upper half scrolls. "Hang up" is the one control that must never end
 * up behind a scroll, so it sits outside the scroll box and stays put on a
 * short screen.
 *
 * The AI panel is NOT in here. It is a sibling column, assembled by
 * `CallWindow`: the two scroll independently, and a transcript that pushed the
 * hang-up button down the page would be this same mistake in a new place. All
 * this file carries is the button that opens it and the note it writes into.
 */
export function LiveCall({
  state,
  queue,
  outcome,
  noteAppend,
  onAddToNote,
  pilotOpen,
  fullscreen,
  canFullscreen,
  onTogglePilot,
  onToggleFullscreen,
  getStreams,
  getAudio,
  onHangup,
  onMute,
  onHold,
  onDigit,
  onTransfer,
  onCancelTransfer,
  onStopRecording,
  onMinimize,
  onDismiss,
  onRedial,
  onNext,
  onStopQueue,
}: {
  state: CallState;
  /** The list being worked down, or null on a single call. Read only by the
   *  ended footer - a call in progress is the same call either way. */
  queue: CallQueue | null;
  outcome: CallOutcomeApi;
  /** A line the AI panel wants in the note; passed straight through. */
  noteAppend: { text: string; at: number } | null;
  /** The same door the other way: a fact from this column asking to be written
   *  down. One handler for both, so a line added here and a line added from the
   *  panel arrive in the note by the same route. */
  onAddToNote: (text: string) => void;
  pilotOpen: boolean;
  /** The window is filling the screen - the button says "come back down". */
  fullscreen: boolean;
  /** There is a screen worth filling. A phone is already as full as it gets. */
  canFullscreen: boolean;
  onTogglePilot: () => void;
  onToggleFullscreen: () => void;
  /** Both halves of the live audio, for the meter behind the clock. */
  getStreams: () => { local: MediaStream | null; remote: MediaStream | null } | null;
  /** Twilio's routing, so a device changed mid-call takes effect mid-call. */
  getAudio: () => CallAudio | null;
  onHangup: () => void;
  onMute: () => void;
  onHold: () => void;
  onDigit: (d: string) => void;
  onTransfer: (to: string, mode: "add" | "handover") => void;
  onCancelTransfer: () => void;
  /** Stop recording this call without ending it, with who asked. */
  onStopRecording: (reason: "objection" | "operator") => void;
  onMinimize: () => void;
  onDismiss: () => void;
  onRedial: () => void;
  onNext: () => void;
  onStopQueue: () => void;
}) {
  const t = useT();
  // One slot, two panels, so only one of them may be open: both take digits, and
  // two number fields on screen at once is a field the operator has to look at
  // to find out which one their typing is going into.
  //
  // The audio panel used to be a third. It is a dropdown now (see
  // `CallControls`) - three device lists in this column made the window taller
  // than the screen and pushed the red button below a scroll.
  const [slot, setSlot] = useState<"none" | "pad" | "transfer">("none");
  const [tones, setTones] = useState("");
  const pad = slot === "pad";
  const transfer = slot === "transfer";

  /** Open a panel, or close it if it is the one already up. Clears the tone
   *  strip either way: it belongs to a keypad that is on its way out. */
  const openSlot = (which: "pad" | "transfer") => {
    setSlot((current) => (current === which ? "none" : which));
    setTones("");
  };

  const phase: CallPhase = state.phase;
  const up = phase === "active";
  const live = phase === "preparing" || phase === "connecting" || phase === "ringing" || phase === "active";
  const elapsed = useElapsed(up ? state.answeredAt : null);
  // The call has been handed to somebody who has actually arrived, so hanging up
  // is the operator LEAVING rather than the call ending. `joined` is the whole
  // condition: a handover whose third party is still ringing is not a handover,
  // and the button is an ordinary hangup until they answer.
  const leaving = state.transfer?.mode === "handover" && state.transfer.joined;

  // An inbound call has no target to read a name off; what it has is whatever
  // the reverse lookup made of the caller's number, and the most specific match
  // is the one the incoming card already showed.
  const who =
    state.target?.companyName ?? state.incoming?.matches[0]?.name ?? t("calls.dial.unknown");
  const number = state.dialedNumber ? formatPhone(state.dialedNumber) : (state.target?.phone ?? "");

  /**
   * The far end's number, unformatted, for the briefing below.
   *
   * `dialedNumber` on a call we placed and the caller's own on one we took. The
   * withheld case is an empty string rather than null in `IncomingCall`, so it
   * is turned back into null here - the panel asks nothing about "".
   */
  const counterpart = state.dialedNumber || state.target?.phone || state.incoming?.from || null;

  // The pill says WHAT happened in three words; `CallErrorBox` below the clock says
  // why, at the length that takes. They used to be the same string, which is how
  // a status capsule ended up holding two sentences of bold red - see
  // `errorStatusKey`.
  //
  // A bad line takes the label off "In call" but NOT off "On hold". While the
  // customer is parked in the conference the operator is not talking to
  // anybody, so "the line is weak" is both true and useless; what they need to
  // know is that somebody is still waiting. Hold wins, the line comes second.
  const status =
    // A takeover on its way in says what is actually happening: the call is
    // already going, and this browser is being put through to it.
    state.takeover && (phase === "preparing" || phase === "connecting" || phase === "ringing")
      ? t("calls.dial.takeoverConnecting")
      : phase === "active"
      ? state.hold
        ? t("calls.dial.onHold")
        : state.link === "lost"
          ? t("calls.dial.linkLost")
          : state.link === "degraded"
            ? t("calls.dial.linkWeak")
            : t("calls.dial.live")
      : phase === "ringing"
        ? // WHOSE PHONE IS RINGING. On a call taken on the operator's mobile the
          // whole of this phase covers two moments: their own phone ringing
          // first, and the customer's only after they have picked it up. The
          // row's status separates them - it is written by the CUSTOMER leg's
          // callbacks, so it does not reach "ringing" until their phone does.
          // Without this the window says "Ringing" while the thing ringing is in
          // the operator's pocket, which is the one instruction this feature
          // cannot afford to get wrong.
          state.viaMobile && state.serverStatus !== "ringing" && state.serverStatus !== "in-progress"
          ? t("calls.dial.ringingMobile")
          : t("calls.dial.ringing")
        : phase === "error"
          ? t(errorStatusKey(state.error))
          : phase === "ended"
            ? t("calls.dial.ended")
            : t("calls.dial.connecting");

  /**
   * A call that never got up has no duration, and 00:00 is not one.
   *
   * The clock read 00:00 on every error screen, which is a stopwatch reporting
   * on a call that did not happen - and on the one error that DOES have a
   * duration behind it (the line dropping mid-conversation, where `disconnect`
   * fills `finalDuration` in while the phase stays `error`) it was wrong rather
   * than merely empty: the call had run for four minutes and the window said
   * none. So the error screen shows the clock when there is something to show
   * and gives the room to the explanation when there is not.
   */
  const duration = phase === "ended" || phase === "error" ? state.finalDuration : elapsed;
  const showClock = phase !== "error" || state.finalDuration !== null;

  return (
    // A fixed width rather than a share of the row, and the panel takes what is
    // left. Sharing it would squeeze this column every time the panel slides in
    // or out - the avatar, the clock and the five buttons would all shuffle
    // sideways for the length of the animation, on the one surface where nothing
    // the operator is reaching for is allowed to move.
    <div className="crm-cp-call relative flex min-h-0 flex-col px-8 pb-7">
      {/* The AI toggle stays on the ended screen too: the transcript and the
          suggestions are worth re-reading while writing up the call. */}
      {live || (phase === "ended" && state.callLogId) ? (
        <div className="absolute right-3.5 top-3.5 z-[2] flex gap-1.5">
          {/* Not on a takeover: the window keeps the panel shut there (see
              `CallWindow`), and a toggle that opens nothing is a dead button. */}
          {state.takeover ? null : (
            <IconBtn
              onClick={onTogglePilot}
              title={t(pilotOpen ? "calls.pilot.close" : "calls.pilot.open")}
              aria-label={t(pilotOpen ? "calls.pilot.close" : "calls.pilot.open")}
              aria-pressed={pilotOpen}
            >
              {pilotOpen ? <PanelRightClose className="size-4" /> : <PanelRightOpen className="size-4" />}
            </IconBtn>
          )}
          {/* Hidden rather than disabled on a phone: a screen the window already
              fills has nothing to offer this button, and a dead control is a
              question the operator has to answer mid-call.

              Corner BRACKETS, not the inward arrows of `Shrink`/`Expand`. This
              button sits next to `Minimize2`, which is two inward arrows, and
              the pair of them at 16px was one icon shown twice - the operator
              would have had to read two tooltips to find out which one puts the
              call in the corner. Brackets read as the edges of a screen; arrows
              read as somewhere to put a thing. */}
          {canFullscreen ? (
            <IconBtn
              onClick={onToggleFullscreen}
              title={t(fullscreen ? "calls.dial.exitFullscreen" : "calls.dial.fullscreen")}
              aria-label={t(fullscreen ? "calls.dial.exitFullscreen" : "calls.dial.fullscreen")}
              aria-pressed={fullscreen}
            >
              {fullscreen ? <Minimize className="size-4" /> : <Maximize className="size-4" />}
            </IconBtn>
          ) : null}
          {live ? (
            <IconBtn onClick={onMinimize} title={t("calls.dial.minimize")} aria-label={t("calls.dial.minimize")}>
              <Minimize2 className="size-4" />
            </IconBtn>
          ) : null}
        </div>
      ) : null}

      <div className="-mx-2 flex min-h-0 flex-col items-center overflow-y-auto px-2 pt-[34px]">
        <Avatar name={who} size={78} className={cn("crm-live-av", up && "is-up")} />
        <span className="mt-[18px] text-[21px] font-bold tracking-[-0.038em]">{who}</span>
        <span className="mt-1.5 text-[12.5px] tabular-nums text-muted-foreground">{number}</span>

        <span
          className={cn(
            "crm-live-st mt-4",
            phase === "error" && "is-bad",
            // Amber, not red: the call is still running and every control still
            // works. Gated on `up` as well as on the grade, so a reading about a
            // live line cannot outlive the line - the provider clears it too.
            up && !state.hold && state.link !== "ok" && "is-warn",
            (phase === "ended" || phase === "preparing" || phase === "connecting") && "is-idle",
          )}
        >
          <i />
          {status}
        </span>

        {/* The clock, standing in its own audio.
            The meter is a background and nothing more: it is behind the digits,
            it is `aria-hidden`, and it carries no information the status line
            above does not already say in words. What it adds is the one thing
            the status line cannot - that the line is open and somebody is
            actually talking on it right now, which is the first thing anybody
            wants to know when a call goes quiet. */}
        {showClock ? (
          <span className="crm-live-clock mt-3.5">
            <CallWave active={up} paused={state.hold} getStreams={getStreams} />
            <b className="relative text-[33px] font-bold tabular-nums tracking-[-0.042em]">
              {fmtDuration(duration)}
            </b>
          </span>
        ) : null}

        {/* Under the clock rather than over it, and that order is deliberate:
            the avatar, the name and the number are WHO, and the sentence is
            about the call. Slotting it above would push the identity of the
            person who was not reached off the top of a 470px card. */}
        {phase === "error" ? <CallErrorBox error={state.error} /> : null}

        {/* The dot, and the button that puts it out. Its own component because
            the announcement's offer to stop has a confirmation, a reason and
            three outcomes behind it - see RecordingStop.tsx. */}
        {live ? <RecordingStop state={state} onStop={onStopRecording} /> : null}

        {up ? (
          <CallControls
            state={state}
            padOpen={pad}
            transferOpen={transfer}
            getAudio={getAudio}
            onMute={onMute}
            onHold={onHold}
            onTogglePad={() => openSlot("pad")}
            onToggleTransfer={() => openSlot("transfer")}
          />
        ) : null}

        {/* A failed hold has to say so. The phase stays `active` - the two of
            them are still talking - so the status line above cannot carry it,
            and an operator who pressed Hold and saw nothing change would
            reasonably believe the customer cannot hear them. */}
        {up && state.error === "hold_failed" ? (
          <p className="mt-2.5 text-xs font-[600] text-[var(--color-warning,var(--color-destructive))]" role="alert">
            {t("calls.errors.holdFailed")}
          </p>
        ) : null}

        {/* The keypad takes the notes slot while it is open, so the window keeps
            one shape instead of growing by a block. */}
        {up && pad ? (
          <div className="mt-[22px] w-full">
            <div
              className={cn(
                "mb-3 grid h-[46px] place-items-center rounded-full bg-[var(--crm-sunk)] tabular-nums",
                tones ? "text-[17px] font-bold tracking-[0.18em]" : "text-[12.5px] font-semibold text-muted-foreground",
              )}
            >
              {tones || t("calls.dial.tones")}
            </div>
            <div className="grid grid-cols-3 gap-2">
              {DTMF.map((d) => (
                <button
                  key={d}
                  type="button"
                  className="crm-key h-[46px] rounded-2xl"
                  onClick={() => {
                    onDigit(d);
                    setTones((v) => (v + d).slice(-16));
                  }}
                >
                  <b className="text-lg">{d}</b>
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {/* The same slot as the keypad, opened by the fourth control. */}
        {up && transfer ? (
          <TransferPad state={state} onTransfer={onTransfer} onCancel={onCancelTransfer} />
        ) : null}

        {/* The same briefing the AI panel carries, in the call column, and ONLY
            when that panel is not the one carrying it.

            Measured rather than assumed: the panel ships open (`window.pilotOpen`
            defaults to true) and it draws these facts even when the co-pilot is
            switched off - the snapshot returns them whatever `ready` says. So a
            second copy beside it would be the same list twice on every desktop
            call. What is actually missing is the phone, where the panel is
            hidden outright, and the operator who closed it. `pilotOpen` is
            already false in both. */}
        {live && !pilotOpen ? (
          <PhoneContextPanel number={counterpart} className="mt-6" onAddToNote={onAddToNote} />
        ) : null}

        {/* In the scrolling half rather than in the footer with the buttons,
            and next to the note for a reason: "how did it go" and "what was
            said" are the same thought, written a second apart. The footer has
            to stay short enough that the button which continues the run is
            never behind a scroll - the same rule the red button follows. */}
        {phase === "ended" && state.callLogId ? <CallOutcome outcome={outcome} /> : null}

        {/* Also on the ended screen, not only while the call is up: the sentence
            that describes a call is usually finished after hanging up, and the
            row is still there to write it to. Keyed on the call log so a redial
            starts on a blank note instead of inheriting the last call's. */}
        {(up && slot === "none") || (phase === "ended" && state.callLogId) ? (
          <CallNoteBox
            key={state.callLogId ?? "none"}
            callLogId={state.callLogId}
            append={noteAppend}
            className="mt-6"
          />
        ) : null}
      </div>

      {live ? (
        /* THE RED BUTTON STOPS MEANING "END THIS CALL" AFTER A HANDOVER.
           Once the call has been handed to somebody who is really in the room,
           pressing it takes the operator out and leaves the other two talking -
           that is what the server does with it (`hangupPlan`, room: "keep"), and
           a button that still said "Hang up" would be describing a call it was
           no longer ending. The wording is the only honest way to say so before
           it is pressed. */
        <button type="button" className="crm-hangup" onClick={onHangup}>
          {leaving ? <LogOut className="size-4" /> : <PhoneOff className="size-4" />}
          {t(leaving ? "calls.transfer.leave" : "calls.dial.hangup")}
        </button>
      ) : (
        <EndOfCall
          state={state}
          queue={queue}
          outcome={outcome}
          onRedial={onRedial}
          onNext={onNext}
          onStopQueue={onStopQueue}
          onDismiss={onDismiss}
        />
      )}
    </div>
  );
}

/** The corner pill: the same call, tucked away so the CRM stays usable. */
export function CallPill({
  state,
  onOpen,
  onMute,
  onHold,
  onHangup,
}: {
  state: CallState;
  onOpen: () => void;
  onMute: () => void;
  onHold: () => void;
  onHangup: () => void;
}) {
  const t = useT();
  const up = state.phase === "active";
  // Hold is excluded for the same reason as in the window: parked is parked,
  // and the line's grade is not what the operator needs off a pill that size.
  const weak = state.link !== "ok" && !state.hold;
  const elapsed = useElapsed(up ? state.answeredAt : null);
  const who = state.target?.companyName ?? (state.dialedNumber ? formatPhone(state.dialedNumber) : t("calls.dial.unknown"));
  // The same rewording as the full window's red button, and for the same
  // reason: after a handover this control takes the operator out of a call it
  // does not end. A tooltip saying "Hang up" would be describing the old one.
  const leaving = state.transfer?.mode === "handover" && state.transfer.joined;

  return (
    <>
      <button type="button" className="flex items-center gap-2.5 border-0 bg-transparent pr-1.5 text-left" onClick={onOpen}>
        <Avatar name={who} size={38} className={cn("crm-live-av", up && "is-up")} />
        <span>
          <span className="block max-w-[148px] truncate text-[13px] font-bold tracking-[-0.025em]">{who}</span>
          {/* The bad-line case is at its WORST here, not in the open window.
              Minimised, this pill is the operator's whole view of the call, and
              a green equalizer bouncing beside a running clock is the app
              insisting the conversation is fine while neither person can hear
              the other. So when the line goes, the pill stops bouncing and says
              so instead. */}
          <span
            className={cn(
              "mt-[3px] flex items-center gap-[7px] text-[11.5px] font-[650]",
              up && weak
                ? "text-[color-mix(in_oklab,var(--color-warning)_78%,var(--color-foreground))]"
                : up
                  ? "text-[color-mix(in_oklab,var(--color-success)_72%,var(--color-foreground))]"
                  : "text-muted-foreground",
            )}
          >
            {up && weak ? (
              <span className="truncate">
                {t(state.link === "lost" ? "calls.dial.linkLost" : "calls.dial.linkWeak")}
              </span>
            ) : up ? (
              <>
                <i className="crm-eq">
                  <b />
                  <b />
                  <b />
                </i>
                <span className="tabular-nums">{fmtDuration(elapsed)}</span>
              </>
            ) : (
              t("calls.dial.connecting")
            )}
          </span>
        </span>
      </button>
      <span className="crm-pill-sep" />
      <button
        type="button"
        className={cn("crm-pill-b", state.muted && "is-on")}
        onClick={onMute}
        title={t(state.muted ? "calls.dial.unmute" : "calls.dial.mute")}
        aria-label={t(state.muted ? "calls.dial.unmute" : "calls.dial.mute")}
      >
        {state.muted ? <MicOff className="size-4" /> : <Mic className="size-4" />}
      </button>
      {/* Hold belongs in the pill too, and not as a convenience: the row above
          only renders while the phase is `active`, so a held call tucked into
          the corner with no way to resume it is the worst outcome this feature
          can produce. */}
      {state.conference ? (
        <button
          type="button"
          className={cn("crm-pill-b", state.hold && "is-hold")}
          onClick={onHold}
          disabled={state.holdPending}
          title={t(state.hold ? "calls.dial.resume" : "calls.dial.hold")}
          aria-label={t(state.hold ? "calls.dial.resume" : "calls.dial.hold")}
        >
          {state.hold ? <Play className="size-4" /> : <Pause className="size-4" />}
        </button>
      ) : null}
      <button
        type="button"
        className="crm-pill-b"
        onClick={onOpen}
        title={t("calls.dial.restore")}
        aria-label={t("calls.dial.restore")}
      >
        <Phone className="size-4" />
      </button>
      <button
        type="button"
        className="crm-pill-b is-end"
        onClick={onHangup}
        title={t(leaving ? "calls.transfer.leave" : "calls.dial.hangup")}
        aria-label={t(leaving ? "calls.transfer.leave" : "calls.dial.hangup")}
      >
        {leaving ? <LogOut className="size-4" /> : <PhoneOff className="size-4" />}
      </button>
    </>
  );
}
